import { useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";
import { BUILD_ID } from "@/buildInfo";

const CHECK_MS = 5 * 60 * 1000;
const LOOP_GUARD = "ss.reloaded-for";

async function fetchLiveBuild() {
  const r = await fetch(`/version.json?_=${Date.now()}`, { cache: "no-store", headers: { "Cache-Control": "no-cache" } });
  if (!r.ok) throw new Error(String(r.status));
  return (await r.json()).build;
}

export function hardReload() {
  const url = new URL(window.location.href);
  url.searchParams.set("_v", String(Date.now()));
  window.location.replace(url.toString());
}

/** Detects a newer deployment than the bundle currently running.
 *  - On first paint with no user interaction yet → reload silently once (guarded against loops).
 *  - Later (tab refocus / 5-min poll) → show a gentle "new version" bar so nothing typed is lost. */
export function BuildFreshness() {
  const [stale, setStale] = useState(false);

  useEffect(() => {
    let interacted = false;
    const markInteracted = () => { interacted = true; };
    ["keydown", "pointerdown"].forEach((e) => window.addEventListener(e, markInteracted, { once: true, passive: true }));

    const check = async () => {
      try {
        const live = await fetchLiveBuild();
        if (!live || live === BUILD_ID) return;
        const already = sessionStorage.getItem(LOOP_GUARD) === live;
        if (!interacted && !already) {
          sessionStorage.setItem(LOOP_GUARD, live);
          hardReload();
          return;
        }
        setStale(true);
      } catch { /* offline or version.json missing — never block the app */ }
    };
    check();
    const onVisible = () => { if (document.visibilityState === "visible") check(); };
    document.addEventListener("visibilitychange", onVisible);
    const t = setInterval(check, CHECK_MS);
    return () => { document.removeEventListener("visibilitychange", onVisible); clearInterval(t); ["keydown", "pointerdown"].forEach((e) => window.removeEventListener(e, markInteracted)); };
  }, []);

  if (!stale) return null;
  return (
    <div role="status" data-testid="build-freshness-bar" className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[60] ss-card px-4 py-2.5 flex items-center gap-3 shadow-xl border border-gold/50">
      <span className="text-sm text-cocoa">A newer version of SugarSense is ready.</span>
      <button onClick={hardReload} data-testid="build-freshness-refresh" className="ss-pill ss-pill-gold h-9"><RefreshCw size={13} /> Refresh</button>
    </div>
  );
}
